// Type C — map_short_answer: student writes a sentence or two.
// No auto-grading for the POC — we reveal a model answer and ask for a
// quick self-assessment, which becomes the score signal.

import { Check, X as XIcon, ArrowRight } from 'lucide-react';
import Button from '../../ui/Button';
import { cn } from '../../../lib/cn';

export default function MapShortAnswerPanel({
  question,
  state,                 // { textResponse, submitted, selfAssessment }
  onChangeText,
  onSubmit,
  onSelfAssess,
  onNext,
  isLast,
}) {
  const { textResponse, submitted, selfAssessment } = state;
  const canSubmit = textResponse.trim().length > 0;

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 space-y-5 overflow-y-auto">
        <p className="text-base leading-relaxed text-gray-900 dark:text-gray-100">
          {question.prompt}
        </p>

        <textarea
          value={textResponse}
          onChange={(e) => onChangeText(e.target.value)}
          disabled={submitted}
          rows={5}
          spellCheck={false}
          placeholder="Type your answer here…"
          className={cn(
            'w-full rounded-lg border px-3 py-2',
            'text-sm leading-relaxed',
            'bg-white dark:bg-gray-800',
            'border-gray-300 dark:border-gray-600',
            'text-gray-900 dark:text-gray-100',
            'placeholder:text-gray-400 dark:placeholder:text-gray-500',
            'focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500',
            'disabled:bg-gray-50 disabled:text-gray-500 dark:disabled:bg-gray-900 dark:disabled:text-gray-400',
            'resize-none',
          )}
        />

        {submitted && (
          <div className="space-y-3">
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/70 px-4 py-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                Model answer
              </div>
              <p className="text-sm leading-relaxed text-gray-800 dark:text-gray-200">
                {question.model_answer}
              </p>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                Self-assessment
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => onSelfAssess('got_it')}
                  className={cn(
                    'inline-flex items-center justify-center gap-1.5',
                    'px-3 py-2 rounded-lg border text-sm font-medium',
                    'transition-colors duration-150',
                    'focus:outline-none focus:ring-2 focus:ring-gray-400',
                    selfAssessment === 'got_it'
                      ? 'border-green-600 bg-green-50 text-green-900 dark:border-green-500 dark:bg-green-950/40 dark:text-green-200'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600 dark:hover:bg-gray-700/50',
                  )}
                >
                  <Check className="w-4 h-4" />
                  I got it
                </button>
                <button
                  type="button"
                  onClick={() => onSelfAssess('missed')}
                  className={cn(
                    'inline-flex items-center justify-center gap-1.5',
                    'px-3 py-2 rounded-lg border text-sm font-medium',
                    'transition-colors duration-150',
                    'focus:outline-none focus:ring-2 focus:ring-gray-400',
                    selfAssessment === 'missed'
                      ? 'border-red-600 bg-red-50 text-red-900 dark:border-red-500 dark:bg-red-950/40 dark:text-red-200'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600 dark:hover:bg-gray-700/50',
                  )}
                >
                  <XIcon className="w-4 h-4" />
                  I missed something
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
        {submitted ? (
          <Button
            onClick={onNext}
            variant="primary"
            className="w-full"
            disabled={selfAssessment == null}
          >
            {isLast ? 'See results' : 'Next question'}
            <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        ) : (
          <Button
            onClick={onSubmit}
            variant="primary"
            className="w-full"
            disabled={!canSubmit}
          >
            Submit
          </Button>
        )}
      </div>
    </div>
  );
}
